import {
  formatarIdentificacaoObra,
  type ObraIdentificavel,
} from "@/shared/utils/formatar-identificacao-obra";
import type { FilterDefinition, FilterOption, FilterValue } from "@/ds";
import { conflitosAbertos, conflitosDoLocal } from "./carga-de-trabalho.conflicts";
import { OBRA_SITUACAO_CANCELADA } from "./carga-de-trabalho.types";
import type {
  AlocacaoDeLocal,
  AlocacaoDeObra,
  AlocacoesPorLocal,
  AlocacoesPorObra,
  LocalResumo,
  ObraResumo,
} from "./carga-de-trabalho.types";

type LocalDados = AlocacoesPorLocal | AlocacoesPorObra;

/** Colunas filtráveis da Carga de Trabalho — perspectiva Local. */
export const LOCAL_FILTER = {
  local: "local",
  obra: "obra",
  situacao: "situacao",
  status: "status",
  conflito: "conflito",
} as const;

export const EMPTY_LOCAL_FILTERS: FilterValue = {};

const SIM = "sim";
const NAO = "nao";
const SITUACAO_OBRA = "obra";
const SITUACAO_CANCELADA = "cancelada";

const SIM_NAO: FilterOption[] = [
  { value: SIM, label: "Sim" },
  { value: NAO, label: "Não" },
];

const SITUACOES: FilterOption[] = [
  { value: SITUACAO_OBRA, label: "Obra" },
  { value: SITUACAO_CANCELADA, label: "Cancelada" },
];

const STATUS: FilterOption[] = [
  { value: "PLANEJADO", label: "Planejado" },
  { value: "REALIZADO", label: "Realizado" },
];

function obraLabel(obra: ObraIdentificavel): string {
  return formatarIdentificacaoObra(obra);
}

function collectLocais(data: LocalDados | null): LocalResumo[] {
  if (!data) return [];
  if ("locais" in data) return data.locais;
  return data.obras.flatMap((obra) => obra.alocacoes.map((alocacao) => alocacao.local));
}

function collectObras(data: LocalDados | null): ObraResumo[] {
  if (!data) return [];
  if ("locais" in data) {
    return [
      ...data.locais.flatMap((local) => local.alocacoes.map((alocacao) => alocacao.obra)),
      ...data.semLocal,
    ];
  }
  return data.obras;
}

/** Colunas do painel; as opções de Local e Obra vêm dos dados carregados (sem filtro aplicado). */
export function buildLocalFilterColumns(data: LocalDados | null): FilterDefinition[] {
  const locais = new Map(collectLocais(data).map((local) => [local.id, local]));
  const obras = new Map(collectObras(data).map((obra) => [obra.id, obra]));

  return [
    {
      key: LOCAL_FILTER.local,
      label: "Local",
      type: "multiselect",
      options: [...locais.values()]
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
        .map((local) => ({ value: local.id, label: local.nome })),
    },
    {
      key: LOCAL_FILTER.obra,
      label: "Obra",
      type: "multiselect",
      options: [...obras.values()]
        .sort((a, b) => a.codigo.localeCompare(b.codigo, "pt-BR"))
        .map((obra) => ({ value: obra.id, label: obraLabel(obra) })),
    },
    { key: LOCAL_FILTER.situacao, label: "Situação", type: "select", options: SITUACOES },
    { key: LOCAL_FILTER.status, label: "Status", type: "multiselect", options: STATUS },
    { key: LOCAL_FILTER.conflito, label: "Conflito", type: "select", options: SIM_NAO },
  ];
}

export function hasLocalFilters(value: FilterValue): boolean {
  return Object.values(value).some((values) => values.length > 0);
}

/** Situação = Cancelada só mostra algo com o indicador Cancelada ligado. */
export function filtraObraCancelada(value: FilterValue): boolean {
  return value[LOCAL_FILTER.situacao]?.[0] === SITUACAO_CANCELADA;
}

function selected(value: FilterValue, key: string): string[] {
  return value[key] ?? [];
}

function matchesSituacao(obra: ObraResumo, value: FilterValue): boolean {
  const [situacao] = selected(value, LOCAL_FILTER.situacao);
  if (!situacao) return true;
  return (situacao === SITUACAO_CANCELADA) === (obra.situacao === OBRA_SITUACAO_CANCELADA);
}

function matchesObra(obra: ObraResumo, value: FilterValue): boolean {
  const obras = selected(value, LOCAL_FILTER.obra);
  if (obras.length > 0 && !obras.includes(obra.id)) return false;
  return matchesSituacao(obra, value);
}

function matchesStatus(status: string | null, value: FilterValue): boolean {
  const statuses = selected(value, LOCAL_FILTER.status);
  return statuses.length === 0 || (status !== null && statuses.includes(status));
}

function filterPorLocal(data: AlocacoesPorLocal, value: FilterValue): AlocacoesPorLocal {
  const locais = selected(value, LOCAL_FILTER.local);
  const [conflito] = selected(value, LOCAL_FILTER.conflito);
  const filtraAlocacoes =
    selected(value, LOCAL_FILTER.obra).length > 0 ||
    selected(value, LOCAL_FILTER.status).length > 0 ||
    selected(value, LOCAL_FILTER.situacao).length > 0;

  const filtrados = data.locais
    .filter((local) => {
      if (locais.length > 0 && !locais.includes(local.id)) return false;
      const abertos = conflitosAbertos(conflitosDoLocal(local.alocacoes, data.conflitosIgnorados));
      if (conflito && (conflito === SIM) !== abertos.length > 0) return false;
      return true;
    })
    .map((local) => ({
      ...local,
      alocacoes: local.alocacoes.filter(
        (alocacao: AlocacaoDeObra) =>
          matchesObra(alocacao.obra, value) && matchesStatus(alocacao.status, value),
      ),
    }))
    .filter((local) => !filtraAlocacoes || local.alocacoes.length > 0);

  // Obras sem Local não têm Local, alocação nem status: só Situação e Obra se aplicam.
  const semLocal =
    locais.length > 0 || conflito || selected(value, LOCAL_FILTER.status).length > 0
      ? []
      : data.semLocal.filter((obra) => matchesObra(obra, value));

  return { ...data, locais: filtrados, semLocal };
}

/**
 * Na visão por Obra o Conflito não se aplica: a consulta pode vir recortada pela busca e
 * pelo período, então o conflito do Local não é confiável de calcular por obra.
 */
function filterPorObra(data: AlocacoesPorObra, value: FilterValue): AlocacoesPorObra {
  const locais = selected(value, LOCAL_FILTER.local);

  const obras = data.obras
    .filter((obra) => matchesObra(obra, value))
    .map((obra) => ({
      ...obra,
      alocacoes: obra.alocacoes.filter(
        (alocacao: AlocacaoDeLocal) =>
          (locais.length === 0 || locais.includes(alocacao.local.id)) &&
          matchesStatus(alocacao.status, value),
      ),
    }))
    .filter(
      (obra) =>
        obra.alocacoes.length > 0 ||
        (locais.length === 0 && selected(value, LOCAL_FILTER.status).length === 0),
    );

  return { ...data, obras };
}

/** Aplica os filtros do painel sobre os dados carregados (no cliente). */
export function applyLocalFilters<T extends LocalDados>(data: T, value: FilterValue): T {
  if (!hasLocalFilters(value)) return data;
  const filtered = "locais" in data ? filterPorLocal(data, value) : filterPorObra(data, value);
  return filtered as T;
}
